'use client';

import Link from 'next/link';
import { ArrowLeft, FileText, Home } from 'lucide-react';
import { Logo } from '@/components/ui/Logo';
import { LandingSilk } from '@/components/landing/LandingSilk';
import { useI18n } from '@/contexts/I18nContext';
import { legalPages, type LegalBlock } from '@/lib/i18n/legal';

type LegalPageKey = keyof typeof legalPages;

interface LegalDocumentPageProps {
  page: LegalPageKey;
  updated?: string;
}

function renderBlock(block: LegalBlock, index: number) {
  if (block.type === 'h3') {
    return (
      <h3 key={index} className="mb-3 mt-6 text-lg text-white/90 sm:text-xl">
        {block.text}
      </h3>
    );
  }

  if (block.type === 'list') {
    return (
      <ul key={index} className="mb-5 list-disc space-y-2.5 pl-5 text-sm leading-7 text-white/58 marker:text-brand-200 sm:text-[15px]">
        {block.items.map((item) => (
          <li key={item} className="pl-1">{item}</li>
        ))}
      </ul>
    );
  }

  return (
    <p key={index} className="mb-5 text-sm leading-7 text-white/58 sm:text-[15px]">
      {block.text}
    </p>
  );
}

export function LegalDocumentPage({ page, updated }: LegalDocumentPageProps) {
  const { locale, t } = useI18n();
  const content = legalPages[page][locale];
  const dateLocale = locale === 'kk' ? 'kk-KZ' : locale === 'ru' ? 'ru-RU' : locale === 'tr' ? 'tr-TR' : locale === 'zh' ? 'zh-CN' : locale === 'ar' ? 'ar-SA' : 'en-US';
  // Prefer the document's own revision date (set per locale in legal.ts) over
  // today's date — a legal text must show when it was actually revised, not the
  // day the page happens to be opened. An explicit `updated` prop still wins.
  const updatedDate =
    updated ??
    content.updatedValue ??
    new Date().toLocaleDateString(dateLocale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  const navigation: Array<{ key: LegalPageKey; label: string; href: string }> = [
    { key: 'offer', label: t('common.offer'), href: '/offer' },
    { key: 'terms', label: t('common.terms'), href: '/terms' },
    { key: 'privacy', label: t('common.privacy'), href: '/privacy' },
    { key: 'payment', label: t('common.payment'), href: '/payment' },
    { key: 'refund', label: t('common.refund'), href: '/refund' },
  ];

  return (
    <main id="new-legal-page" className="new-landing-shell relative min-h-screen overflow-hidden bg-[#09031a] text-white">
      {/* Единый брендовый фон — тот же WebGL-silk, что на главной. */}
      <LandingSilk
        baseColor="#28137F"
        highlightColor="#4144E6"
        className="pointer-events-none fixed inset-0 z-0"
      />
      <div className="fixed inset-0 z-0 bg-black/35" />
      <div className="fixed inset-0 z-0 bg-gradient-to-b from-black/10 via-transparent to-black/65" />

      <div className="relative z-10 mx-auto w-full max-w-6xl px-3 py-3 sm:px-6 sm:py-5 lg:px-8 lg:py-6">
        <header className="mb-4 flex items-center justify-between sm:mb-6">
          <Link href="/new-landing" aria-label={t('contact.backHome')} className="transition-transform hover:scale-[1.02]">
            <Logo className="h-auto w-40 text-white sm:w-52" color="currentColor" />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              href="/contact"
              className="liquid-glass inline-flex h-11 items-center gap-2 rounded-full px-4 text-xs font-medium text-white/75 transition-transform hover:scale-105 hover:text-white"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t('common.contact')}</span>
            </Link>
            <Link
              href="/new-landing"
              aria-label={t('contact.backHome')}
              title={t('contact.backHome')}
              className="liquid-glass inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-white/75 transition-transform hover:scale-105 hover:text-white"
            >
              <Home className="h-4 w-4" />
            </Link>
          </div>
        </header>

        <nav
          aria-label={content.kicker}
          className="liquid-glass-strong sticky top-3 z-30 mb-4 overflow-x-auto rounded-[1.5rem] p-1.5 shadow-[0_18px_50px_rgba(0,0,0,0.16)] sm:top-5 sm:mb-6"
        >
          <div className="flex min-w-max items-center gap-1">
            {navigation.map((item) => {
              const isActive = item.key === page;

              return (
                <Link
                  key={item.key}
                  href={item.href}
                  aria-current={isActive ? 'page' : undefined}
                  className={`rounded-full px-4 py-2.5 text-xs font-medium transition-[background-color,color,box-shadow,transform] hover:scale-[1.02] sm:px-5 ${
                    isActive
                      ? 'bg-white/[0.16] text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.28),0_8px_22px_rgba(60,29,155,0.18)]'
                      : 'text-white/52 hover:bg-white/[0.08] hover:text-white/85'
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </div>
        </nav>

        <article className="liquid-glass-strong rounded-[2rem] px-5 py-7 sm:px-9 sm:py-10 lg:px-12 lg:py-12">
          <header className="mb-10 border-b border-white/10 pb-8 sm:mb-12 sm:pb-10">
            <div className="flex items-center gap-3 text-[10px] font-medium uppercase tracking-[0.24em] text-white/42">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-brand-500/22 text-brand-100">
                <FileText className="h-4 w-4" />
              </span>
              {content.kicker}
            </div>
            <h1 className="mt-5 max-w-4xl text-4xl leading-[1.02] tracking-[-0.045em] sm:text-5xl lg:text-6xl">
              {content.title}
            </h1>
            <p className="mt-4 text-xs text-white/38">
              {content.updatedLabel}: {updatedDate}
            </p>
          </header>

          <div className="mx-auto max-w-4xl">
            {content.sections.map((section, sectionIndex) => (
              <section
                key={section.title}
                className="grid gap-4 border-b border-white/[0.08] py-8 first:pt-0 last:border-b-0 last:pb-0 sm:grid-cols-[48px_1fr] sm:gap-6"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-white/[0.08] text-xs font-medium text-white/48 shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]">
                  {String(sectionIndex + 1).padStart(2, '0')}
                </span>
                <div>
                  <h2 className="mb-5 text-2xl leading-tight tracking-[-0.025em] text-white/92 sm:text-3xl">
                    {section.title}
                  </h2>
                  {section.blocks.map(renderBlock)}
                </div>
              </section>
            ))}
          </div>
        </article>

        <footer className="flex flex-col gap-3 px-2 pb-3 pt-6 text-[10px] text-white/35 sm:flex-row sm:items-center sm:justify-between">
          <span>{t('common.companyLegalName')} · {t('common.companyBin')}</span>
          <a href="mailto:legal@cannect.ai" className="transition-colors hover:text-white/75">legal@cannect.ai</a>
        </footer>
      </div>
    </main>
  );
}
